import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Lock, RefreshCw } from 'lucide-react';
import { API_BASE_URL } from '../lib/utils';
import EventDisclosureReportView, {
  downloadDisclosureJson,
  printDisclosureReport,
} from '../components/command/EventDisclosureReportView';
import type { EventDisclosureReport } from '../types/disclosure';

export default function EventDisclosurePublicPage() {
  const { token } = useParams<{ token: string }>();
  const [meta, setMeta] = useState<{ eventName: string; label?: string } | null>(null);
  const [report, setReport] = useState<EventDisclosureReport | null>(null);
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(true);
  const [unlocking, setUnlocking] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!token) return;
    setLoading(true);
    fetch(`${API_BASE_URL}/disclosure/${token}`)
      .then((r) => r.json().then((d) => ({ ok: r.ok, status: r.status, d })))
      .then(({ ok, status, d }) => {
        if (!ok) throw new Error(d.error || (status === 410 ? 'Link expired' : 'Link not found'));
        setMeta({ eventName: d.eventName, label: d.label });
      })
      .catch((e) => setError(e instanceof Error ? e.message : 'Link not found'))
      .finally(() => setLoading(false));
  }, [token]);

  const unlock = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!token || !password.trim()) return;
    setUnlocking(true);
    setError(null);
    try {
      const res = await fetch(`${API_BASE_URL}/disclosure/${token}/unlock`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ password: password.trim() }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Could not unlock disclosure');
      setReport(data.disclosure);
      if (data.share?.eventName) setMeta({ eventName: data.share.eventName, label: data.share.label });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Incorrect password');
    } finally {
      setUnlocking(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background text-sm text-muted-foreground">
        <RefreshCw className="w-4 h-4 animate-spin mr-2" />
        Loading…
      </div>
    );
  }

  if (error && !meta && !report) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-6">
        <div className="max-w-md w-full text-center space-y-2">
          <p className="text-destructive font-medium">{error}</p>
          <p className="text-xs text-muted-foreground">Contact the organisation that shared this link.</p>
        </div>
      </div>
    );
  }

  if (!report) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-6">
        <div className="max-w-md w-full border border-border rounded-2xl p-6 shadow-lg bg-card">
          <div className="flex items-center gap-2 mb-1">
            <Lock className="w-4 h-4 text-primary" />
            <h1 className="text-sm font-bold">Event disclosure</h1>
          </div>
          <p className="text-xs text-muted-foreground mb-4">
            {meta?.label ? `${meta.label} — ` : ''}
            {meta?.eventName}
          </p>
          <form onSubmit={unlock} className="space-y-3">
            <div>
              <label htmlFor="disclosure-password" className="text-[10px] uppercase text-muted-foreground">
                Password
              </label>
              <input
                id="disclosure-password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="current-password"
                className="mt-1 w-full px-3 py-2 rounded-lg border border-border bg-background text-sm"
                placeholder="Enter the password provided to you"
              />
            </div>
            {error && <p className="text-xs text-destructive">{error}</p>}
            <button
              type="submit"
              disabled={unlocking || !password.trim()}
              className="w-full py-2.5 rounded-lg bg-primary text-primary-foreground text-sm font-semibold disabled:opacity-50"
            >
              {unlocking ? 'Verifying…' : 'View disclosure report'}
            </button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="p-6 max-w-4xl mx-auto">
        <EventDisclosureReportView
          report={report}
          onPrint={printDisclosureReport}
          onDownload={() => downloadDisclosureJson(report)}
        />
      </div>
    </div>
  );
}
